/* eslint-disable no-underscore-dangle */
import { CaretRightOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Select, Slider } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import {
  useContext,
} from 'react';
import ProductsContext from 'contexts/ProductsContext';
import MainAppContext from 'contexts/MainAppContext';
import TimeSeriesContext from 'contexts/TimeSeriesContext';
import { ReactComponent as TimeBackIcon } from 'assets/icons/timeBack.svg';
import Icon from '@ant-design/icons/lib/components/Icon';
import ADSBContext from 'contexts/ADSBContext';
import { createLogger } from 'services/debugLogger';
import { clearPlaybackCache } from 'utils/imageCaches';
import styles from './index.module.scss';
import CustomDateRangePicker from './components/CustomDateRangePicker';
import RealTimeContext from '../../../../../../contexts/RealTimeContext';
import { calculateRanges, generateTimestampList, utcDayjs } from '../../../../../../utils';
import { useGlobalMapCommands } from '../../../../../../contexts/GlobalMapCommandsContext';
import useValidDatesRanges from '../../../../../../utils/hooks/useValidDatesRanges';

const logger = createLogger('TimeSeriesPlayer');

const { Option } = Select;

const {
  wrapper, sliderWrapper, tooltip, currentMomentWrapper,
  speedWrapper, isAValidDate, rangeDatePickerWrapper, timeBackIcon,
} = styles;

function TimeSeriesPlayer() {
  const {
    activeProducts, selectedProducts, setActiveProductsTimes,
  } = useContext(ProductsContext);
  const { playbackMode, realTimeMode, setRealTimeMode } = useContext(MainAppContext);
  const { triggerPreloadAllLayers } = useGlobalMapCommands();
  const {
    play,
    stop,
    frameRating,
    setFrameRating,
    range,
    setRange,
    setCurrentPosition,
    currentPosition,
    preloadRange,
    setPreloadRange,
    setPreloadingState,
    setTimestampList,
  } = useContext(TimeSeriesContext);
  const { realTime } = useContext(RealTimeContext);
  const { isConnected: adsbIsConnected } = useContext(ADSBContext);
  // eslint-disable-next-line max-len
  const maxDaysLimit: string | undefined = (window as any)._env_.REACT_APP_MAX_DAYS_LIMIT_TO_SHOW_DATA;

  const validDatesRanges = useValidDatesRanges(selectedProducts);

  const { samplingDuration } = calculateRanges(
    preloadRange || range,
  );

  const handleOnButtonNowClick = () => {
    stop();

    if (realTimeMode) {
      setCurrentPosition(utcDayjs(range?.[0])?.toISOString() || utcDayjs().toISOString());
    }
    setRealTimeMode((prev) => !prev);
  };

  const handleOnPlayStopButtonClick = () => {
    if (realTimeMode) {
      setCurrentPosition(utcDayjs(range?.[0])?.toISOString() || utcDayjs().toISOString());
    }
    setRealTimeMode(() => false);
    if (playbackMode) {
      stop();
    } else {
      play();
    }
  };

  return (
    <div className={wrapper}>
      <div className={currentMomentWrapper}>
        <span>
          {realTimeMode && realTime ? realTime?.format('YYYY-MM-DD HH:mm:ss') : utcDayjs(currentPosition)?.format('YYYY-MM-DD HH:mm:ss')}
          <small> UTC</small>
        </span>
      </div>
      <div>
        <Button
          onClick={handleOnButtonNowClick}
          icon={<CaretRightOutlined />}
          // disabled={Boolean(!activeProducts.length)}
          type={realTimeMode ? 'primary' : 'default'}
        >
          Real-Time
        </Button>
        <Button
          type={playbackMode ? 'primary' : 'default'}
          icon={playbackMode
            ? <StopOutlined /> : <Icon component={TimeBackIcon} className={timeBackIcon} />}
          disabled={activeProducts.length === 0}
          onClick={handleOnPlayStopButtonClick}
        >
          {playbackMode ? 'Stop' : 'Replay'}
        </Button>
        <div className={speedWrapper}>
          <small>Speed:</small>
          <Select defaultValue="1" value={frameRating} onChange={setFrameRating}>
            <Option value="1">1x</Option>
            <Option value="2">2x</Option>
            <Option value="5">5x</Option>
            <Option value="10">10x</Option>
          </Select>
        </div>
        <div className={sliderWrapper}>
          <Slider
            defaultValue={0}
            max={range?.[1]?.valueOf()}
            min={range?.[0]?.valueOf()}
            value={utcDayjs(currentPosition)?.valueOf()}
            step={samplingDuration}
            disabled={activeProducts.length === 0 && !adsbIsConnected}
            onChange={(value) => {
              stop();
              setRealTimeMode(() => false);
              setCurrentPosition(utcDayjs(value).toISOString());
              setActiveProductsTimes((prev) => {
                const newObject = { ...prev };
                return Object
                  .keys(prev)
                  .map((v) => parseInt(v, 10))
                  .reduce<Record<number, Dayjs | undefined>>((acc, cur) => {
                    if (prev[cur]) {
                      acc[cur] = utcDayjs(value);
                    }
                    return acc;
                  }, { ...newObject });
              });
            }}
            tooltip={{
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: (data) => (
                <div className={tooltip}>
                  <div>{utcDayjs(data).format('HH:mm:ss')}</div>
                  <div>{utcDayjs(data).format('YYYY-MM-DD')}</div>
                </div>
              ),
            }}
          />
        </div>
        <div className={rangeDatePickerWrapper}>
          <CustomDateRangePicker
            key={validDatesRanges.toString()}
            range={range}
            onOk={(event, picker) => {
              let startLocal;
              let endLocal = dayjs();
              switch (picker.chosenLabel) {
                case '30 minutes':
                  startLocal = dayjs().subtract(30, 'minute');
                  break;
                case '1 hour':
                  startLocal = dayjs().subtract(1, 'hour');
                  break;
                case '2 hours':
                  startLocal = dayjs().subtract(2, 'hour');
                  break;
                case '3 hours':
                  startLocal = dayjs().subtract(3, 'hour');
                  break;
                case '24 hours':
                  startLocal = dayjs().subtract(1, 'day');
                  break;
                default:
                  startLocal = dayjs(picker.startDate.toISOString());
                  endLocal = dayjs(picker.endDate.toISOString());
                  break;
              }
              const startDate = dayjs.utc(startLocal.format('YYYY-MM-DDTHH:mm:ss'));
              const endDate = dayjs.utc(endLocal.format('YYYY-MM-DDTHH:mm:ss'));
              if (realTimeMode) {
                setRealTimeMode(() => false);
              }
              stop();
              // New range: frames preloaded for the previous one are unreachable.
              clearPlaybackCache();
              setRange([startDate, endDate]);
              setPreloadRange([startDate, endDate]);
              setPreloadingState((prev) => prev.map((s) => (s ? 'ready' : s)));
              setCurrentPosition(startDate.toISOString());
              const { samplingDuration } = calculateRanges([startDate, endDate]);

              const generatedTimestampList = generateTimestampList(
                [startDate, endDate],
                samplingDuration,
              );
              setTimestampList(generatedTimestampList);
              logger.info(`Calling triggerPreloadAllLayers with ${generatedTimestampList.length} timestamps`);
              triggerPreloadAllLayers(generatedTimestampList);
            }}
            isCustomDateFn={(date: Dayjs) => {
              const parseData = utcDayjs(date.toDate());
              const maxDays = maxDaysLimit || ' ';
              const maxDaysLimitNumber = parseInt(maxDays, 10);
              const isValid = validDatesRanges.reduce<boolean>((acc, range) => {
                if (parseData.isBetween(range[0], range[1], 'day', '[]') && (!Number.isNaN(maxDaysLimitNumber) ? utcDayjs().diff(date, 'days') <= maxDaysLimitNumber : true)) {
                  return true;
                }
                return acc;
              }, false);
              return isValid ? [isAValidDate] : [];
            }}
            isInvalidDateFn={(date: Dayjs) => {
              if (maxDaysLimit) {
                const maxDaysLimitNumber = parseInt(maxDaysLimit, 10);
                if (!Number.isNaN(maxDaysLimitNumber)) return utcDayjs().diff(date, 'days') > parseInt(maxDaysLimit, 10);
                return false;
              }
              return false;
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default TimeSeriesPlayer;
